import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft,
  CheckCircle,
  XCircle,
  Package,
  AlertTriangle,
  User,
  Calendar,
  Clock,
  Info,
  Send
} from 'lucide-react';
import { API_URL } from '../../Config';

// Composant Toast
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 4000);

    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-emerald-500' : 
                  type === 'error' ? 'bg-rose-500' : 
                  type === 'warning' ? 'bg-amber-500' : 
                  'bg-blue-500';

  const Icon = type === 'success' ? CheckCircle : 
               type === 'error' ? XCircle : 
               type === 'warning' ? AlertTriangle : Info;

  return (
    <div className={`fixed top-6 right-6 z-50 px-4 py-3 ${bgColor} text-white rounded-lg shadow-lg transform transition-transform duration-300 animate-slide-in`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Icon className="w-5 h-5 mr-2" />
          <span className="font-medium">{message}</span>
        </div>
        <button 
          onClick={onClose}
          className="ml-4 text-white hover:text-gray-200 focus:outline-none"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

// Composant de confirmation
const ConfirmDialog = ({ isOpen, title, message, action, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  const isValidation = action === 'valider';
  const bgColor = isValidation ? 'bg-emerald-600' : 'bg-rose-600';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-black/40"
        onClick={onCancel}
      />
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">{title}</h3>
          <p className="text-gray-600 text-sm">{message}</p>
        </div>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 text-sm font-medium text-white ${bgColor} hover:opacity-90 rounded-lg transition-colors`}
          >
            Confirmer
          </button>
        </div>
      </div>
    </div>
  );
};

export default function ValiderDemande() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [demande, setDemande] = useState(null);
  const [quantites, setQuantites] = useState({});
  const [action, setAction] = useState('valider');
  const [motifRefus, setMotifRefus] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [confirm, setConfirm] = useState({ 
    show: false, 
    title: '', 
    message: '', 
    action: '',
    onConfirm: null 
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast({ ...toast, show: false });
  };

  const showConfirm = (title, message, action, onConfirm) => {
    setConfirm({ show: true, title, message, action, onConfirm });
  };

  const hideConfirm = () => {
    setConfirm({ ...confirm, show: false });
  };

  useEffect(() => {
    loadDemande();
  }, [id]);

  const loadDemande = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/validation/demandes/${id}`, { headers });
      
      if (res.data.success) {
        const d = res.data.data;
        setDemande(d);
        initializeQuantities(d);
      }
    } catch (err) {
      console.error('Erreur:', err);
      
      if (err.response?.status === 400) {
        showToast('Cette demande ne peut pas être validée (déjà traitée ou inaccessible)', 'warning');
      } else if (err.response?.status === 404) {
        showToast(`Demande #${id} introuvable`, 'error');
      } else {
        showToast('Erreur lors du chargement de la demande', 'error');
      }
      
      setTimeout(() => navigate('/validateur'), 2000);
    } finally {
      setLoading(false);
    }
  };

  const initializeQuantities = (demande) => {
    const init = {};
    if (demande.demande_materiels?.length > 0) {
      demande.demande_materiels.forEach(item => {
        if (item.materiel) {
          init[item.materiel_id] = {
            quantite_validee: item.quantite_demandee || 0,
            quantite_demandee: item.quantite_demandee || 0,
            stock_actuel: item.materiel.stock || 0,
            nom: item.materiel.nom || 'Inconnu',
            unite: item.materiel.unite || 'unité'
          };
        }
      });
    }
    setQuantites(init);
  };

  const handleQuantityChange = (materielId, value) => {
    const numValue = parseInt(value) || 0;
    const stockMax = quantites[materielId]?.stock_actuel || 0;
    
    if (numValue > stockMax) {
      setQuantites(prev => ({ 
        ...prev, 
        [materielId]: { ...prev[materielId], quantite_validee: stockMax }
      }));
      showToast(`Quantité maximale: ${stockMax} ${quantites[materielId]?.unite}`, 'warning');
    } else {
      setQuantites(prev => ({ 
        ...prev, 
        [materielId]: { ...prev[materielId], quantite_validee: numValue }
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (action === 'refuser' && !motifRefus.trim()) {
      newErrors.motifRefus = 'Veuillez indiquer le motif du refus';
      showToast('Veuillez indiquer le motif du refus', 'error');
      return false;
    }

    if (action === 'valider') {
      const totalValidated = Object.values(quantites).reduce(
        (sum, data) => sum + (parseInt(data.quantite_validee) || 0), 0
      );
      if (totalValidated === 0) {
        showToast('Vous devez valider au moins un article', 'error');
        return false;
      }
    }

    return true;
  };

  const handleSubmitConfirmation = () => {
    if (!validateForm()) return;

    const actionText = action === 'valider' ? 'Valider' : 'Refuser';
    const message = action === 'valider' 
      ? 'Êtes-vous sûr de vouloir valider cette demande ?'
      : `Êtes-vous sûr de vouloir refuser cette demande ?${motifRefus ? `\nMotif: "${motifRefus}"` : ''}`;

    showConfirm(
      `${actionText} la demande`,
      message,
      action,
      () => {
        hideConfirm();
        processDemande();
      }
    );
  };

  const processDemande = async () => {
    setSubmitting(true);

    try {
      const payload = { action };

      if (action === 'refuser') {
        payload.motif_refus = motifRefus.trim();
      }

      if (action === 'valider') {
        const materiels = {};
        Object.entries(quantites).forEach(([matId, data]) => {
          materiels[matId] = { quantite_validee: parseInt(data.quantite_validee) || 0 };
        });
        payload.materiels = materiels;
      }

      const response = await axios.post(
        `${API_URL}/validation/demandes/${id}/validate`,
        payload,
        { headers }
      );

      if (response.data.success) {
        showToast(`Demande ${action === 'valider' ? 'validée' : 'refusée'} avec succès`, 'success');
        setTimeout(() => navigate('/validateur'), 1500);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Erreur lors du traitement';
      showToast(errorMsg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement de la demande...</p>
        </div>
      </div>
    );
  }

  if (!demande) {
    return null;
  }

  const totalDemande = demande.demande_materiels?.reduce((sum, item) => sum + (item.quantite_demandee || 0), 0) || 0;
  const totalValide = Object.values(quantites).reduce((sum, data) => sum + (parseInt(data.quantite_validee) || 0), 0);

  return (
    <>
      {/* Toast Notification */}
      {toast.show && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={hideToast}
        />
      )}

      {/* Confirmation Dialog */}
      {confirm.show && (
        <ConfirmDialog
          isOpen={confirm.show}
          title={confirm.title}
          message={confirm.message}
          action={confirm.action}
          onConfirm={confirm.onConfirm}
          onCancel={hideConfirm}
        />
      )}

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start gap-4 mb-6">
            <button
              onClick={() => navigate('/validateur')}
              className="text-blue-600 hover:text-blue-800 flex items-center gap-2 text-sm font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Validation de la demande #{demande.numero || demande.id}
              </h1>
              <div className="flex flex-wrap gap-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <User className="w-4 h-4" />
                  {demande.demandeur?.nom || 'N/A'}
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Calendar className="w-4 h-4" />
                  {new Date(demande.created_at).toLocaleDateString('fr-FR')}
                </div>
                <div className="flex items-center gap-2 text-sm text-amber-600">
                  <Clock className="w-4 h-4" />
                  En attente de validation
                </div>
              </div>
            </div>
          </div>

          {/* Statistiques */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-semibold text-gray-900">
                {demande.demande_materiels?.length || 0}
              </div>
              <div className="text-sm text-gray-500">Articles</div>
            </div>
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-semibold text-blue-600">{totalDemande}</div>
              <div className="text-sm text-gray-500">Total demandé</div>
            </div>
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-semibold text-emerald-600">{totalValide}</div>
              <div className="text-sm text-gray-500">Total validé</div>
            </div>
          </div>
        </div>

        {/* Liste des matériels */}
        <div className="bg-white rounded-xl border mb-8">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-600" />
              Matériels demandés
            </h2>
          </div>

          <div className="divide-y">
            {demande.demande_materiels?.map((item, index) => {
              if (!item.materiel) return null;
              
              const demandeQty = item.quantite_demandee || 0;
              const valideQty = quantites[item.materiel_id]?.quantite_validee || 0;
              const stock = item.materiel.stock || 0;
              const unite = item.materiel.unite || 'unité';
              const stockStatus = stock < demandeQty ? 'low' : stock >= demandeQty ? 'good' : 'out';
              
              return (
                <div key={index} className="px-6 py-4">
                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                      <div className="mb-2">
                        <div className="font-medium text-gray-900">{item.materiel.nom}</div>
                        <div className="text-sm text-gray-500">{unite}</div>
                      </div>
                      
                      <div className="flex items-center gap-6">
                        <div className="text-sm">
                          <div className="text-gray-500">Stock disponible</div>
                          <div className={`font-medium ${
                            stockStatus === 'good' ? 'text-emerald-600' : 
                            stockStatus === 'low' ? 'text-amber-600' : 'text-rose-600'
                          }`}>
                            {stock} {unite}
                          </div>
                        </div>
                        <div className="text-sm">
                          <div className="text-gray-500">Quantité demandée</div>
                          <div className="font-medium text-blue-600">{demandeQty} {unite}</div>
                        </div>
                      </div>
                    </div>

                    {action === 'valider' && (
                      <div className="md:w-48">
                        <div className="text-sm font-medium text-gray-700 mb-2">Quantité validée</div>
                        <div className="relative">
                          <input
                            type="number"
                            min="0"
                            max={stock}
                            value={valideQty}
                            onChange={(e) => handleQuantityChange(item.materiel_id, e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                              valideQty > stock ? 'border-rose-300 bg-rose-50' : 'border-gray-300'
                            }`}
                          />
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                            {unite}
                          </div>
                        </div>
                        {valideQty > stock && (
                          <div className="text-xs text-rose-600 mt-1">
                            Dépassement de stock
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Section décision */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Décision</h3>

              <div className="grid grid-cols-2 gap-4 mb-8">
                <button
                  onClick={() => setAction('valider')}
                  className={`px-4 py-3 border rounded-lg flex items-center gap-3 transition-colors ${
                    action === 'valider' 
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700' 
                      : 'border-gray-300 hover: text-gray-700'
                  }`}
                >
                  <CheckCircle className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">Valider</div>
                    <div className="text-xs">Approuver la demande</div>
                  </div>
                </button>

                <button
                  onClick={() => setAction('refuser')}
                  className={`px-4 py-3 border rounded-lg flex items-center gap-3 transition-colors ${
                    action === 'refuser' 
                      ? 'border-rose-500 bg-rose-50 text-rose-700' 
                      : 'border-gray-300 hover: text-gray-700'
                  }`}
                >
                  <XCircle className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">Refuser</div>
                    <div className="text-xs">Refuser la demande</div>
                  </div>
                </button>
              </div>

              {action === 'refuser' && (
                <div className="mb-8">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Motif du refus *
                  </label>
                  <textarea
                    value={motifRefus}
                    onChange={(e) => setMotifRefus(e.target.value)}
                    placeholder="Indiquez le motif du refus..."
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {errors.motifRefus && (
                    <div className="text-sm text-rose-600 mt-2">{errors.motifRefus}</div>
                  )}
                </div>
              )}

              <div className="flex gap-3 pt-6 border-t">
                <button
                  onClick={handleSubmitConfirmation}
                  disabled={submitting}
                  className={`flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-lg transition-colors flex items-center justify-center gap-2 ${
                    action === 'valider' 
                      ? 'bg-emerald-600 hover:bg-emerald-700' 
                      : 'bg-rose-600 hover:bg-rose-700'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                      Traitement...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      {action === 'valider' ? 'Valider la demande' : 'Refuser la demande'}
                    </>
                  )}
                </button>

                <button
                  onClick={() => navigate('/validateur')}
                  className="px-4 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover: transition-colors"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>

          {/* Résumé */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl border p-6">
              <h4 className="font-semibold text-gray-900 mb-4">Résumé</h4>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Articles validés</span>
                  <span className="font-medium">{totalValide}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Différence</span>
                  <span className={`font-medium ${
                    totalDemande === totalValide ? 'text-emerald-600' : 'text-amber-600'
                  }`}>
                    {totalDemande - totalValide}
                  </span>
                </div>
                <div className="pt-3 border-t">
                  <div className="text-xs text-gray-500">
                    La validation sera enregistrée dans l'historique de la demande.
                  </div>
                </div>
              </div>
            </div>

            {Object.values(quantites).some(item => item.quantite_validee > item.stock_actuel) && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-700">
                    Certaines quantités validées dépassent le stock disponible.
                    La demande pourra être partiellement traitée.
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </>
  );
}